import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import {
    getAllEntitesRequest,
    deleteEntiteRequest,
    createEntiteRequest,
    updateEntiteRequest,
    importEntitiesExcelRequest,
    exportEntitiesRequest,
    downloadEntitiesTemplateRequest,
} from "../../../redux/actions/entitesActions";
import Swal from "sweetalert2";
import "./EntiteManagement.css";
import { Pencil, Trash2, Upload, Download, FileSpreadsheet, X, Eye, CheckCircle2, AlertTriangle } from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   MODAL AJOUT / EDIT
═══════════════════════════════════════════════════════════ */
const EntiteModal = ({ isOpen, onClose, onSubmit, editingEntite, loading }) => {
    const [formData, setFormData] = useState({ code: "", name: "", isActive: true });

    useEffect(() => {
        if (editingEntite) {
            setFormData({
                code:     editingEntite.code     || "",
                name:     editingEntite.name     || "",
                isActive: editingEntite.isActive !== undefined ? editingEntite.isActive : true,
            });
        } else {
            setFormData({ code: "", name: "", isActive: true });
        }
    }, [editingEntite, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="jm-overlay" onClick={onClose}>
            <div className="jm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="jm-modal-header">
                    <h3>{editingEntite ? "Modifier l'entité" : "Ajouter une entité"}</h3>
                    <button className="jm-modal-close" onClick={onClose}>✕</button>
                </div>
                <form className="jm-form" onSubmit={handleSubmit}>
                    <div className="jm-grid">
                        <div className="jm-field">
                            <label>Code *</label>
                            <input
                                name="code" value={formData.code} onChange={handleChange}
                                required maxLength={50} placeholder="Ex : ENT-001"
                            />
                        </div>
                        <div className="jm-field">
                            <label>Nom de l'entité *</label>
                            <input
                                name="name" value={formData.name} onChange={handleChange}
                                required maxLength={200} placeholder="Ex : Direction Générale"
                            />
                        </div>
                        <div className="jm-field jm-full">
                            <label className="jm-check-label">
                                <input
                                    type="checkbox" name="isActive"
                                    checked={formData.isActive} onChange={handleChange}
                                />
                                Entité active
                            </label>
                        </div>
                    </div>
                    <div className="jm-modal-actions">
                        <button type="button" className="jm-btn jm-btn-outline" onClick={onClose} disabled={loading}>
                            Annuler
                        </button>
                        <button type="submit" className="jm-btn jm-btn-primary" disabled={loading}>
                            {loading ? "Chargement..." : editingEntite ? "Enregistrer" : "Créer l'entité"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   HELPERS normalisation résultat import
═══════════════════════════════════════════════════════════ */
const normalizeErrors = (importResult) => {
    if (!importResult) return [];
    if (Array.isArray(importResult.errors))    return importResult.errors;
    if (Array.isArray(importResult.errorList)) return importResult.errorList;
    if (Array.isArray(importResult.failures))  return importResult.failures;
    return [];
};

const normalizePreviewRows = (importResult) => {
    if (!importResult) return [];
    if (Array.isArray(importResult.preview)) return importResult.preview;
    if (Array.isArray(importResult.rows))    return importResult.rows;
    if (Array.isArray(importResult.data))    return importResult.data;
    return [];
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const EntiteManagement = ({
                              entites, loading, message, error,
                              bulkLoading, importResult,
                              getAllEntites,
                              createEntite, updateEntite, deleteEntite,
                              importEntitiesExcel, exportEntities, downloadTemplate,
                          }) => {
    const [search,        setSearch]        = useState("");
    const [filter,        setFilter]        = useState("all");
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [editingEntite, setEditingEntite] = useState(null);
    const [importAction,  setImportAction]  = useState("add");
    const [selectedFile,  setSelectedFile]  = useState(null);
    const [fileName,      setFileName]      = useState("");

    // ── flag local : dry run vient d'être demandé ──
    const [dryRunDone, setDryRunDone] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => { getAllEntites(); }, []);

    // Quand le dry run revient du store, on le marque
    useEffect(() => {
        if (importResult && !bulkLoading) {
            setDryRunDone(true);
        }
    }, [importResult, bulkLoading]);

    /* ── CRUD ───────────────────────────────────── */
    const openCreate = () => { setEditingEntite(null); setIsModalOpen(true); };
    const openEdit   = (e) => { setEditingEntite(e);   setIsModalOpen(true); };
    const closeModal = ()  => { setIsModalOpen(false);  setEditingEntite(null); };

    const handleSubmit = (formData) => {
        editingEntite ? updateEntite(editingEntite.id, formData) : createEntite(formData);
        closeModal();
    };

    const handleDelete = (id, name) => {
        Swal.fire({
            title: "Suppression",
            text: `Supprimer "${name}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor:  "#6b7280",
            confirmButtonText:  "Oui, supprimer",
            cancelButtonText:   "Annuler",
        }).then((res) => { if (res.isConfirmed) deleteEntite(id); });
    };

    /* ── IMPORT ─────────────────────────────────── */
    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        setFileName(file.name);
        setDryRunDone(false); // reset résultat précédent si on change de fichier
    };

    const clearFile = () => {
        setSelectedFile(null);
        setFileName("");
        setDryRunDone(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // Vérifier (dry run)
    const handleVerify = () => {
        if (!selectedFile) {
            Swal.fire({ icon: "warning", title: "Aucun fichier", text: "Sélectionnez un fichier Excel." });
            return;
        }
        setDryRunDone(false);
        importEntitiesExcel(selectedFile, true, importAction);
    };

    // Confirmer l'import réel
    const handleConfirmImport = () => {
        if (!selectedFile) return;
        Swal.fire({
            title: "Confirmer l'import",
            text: "Voulez-vous importer les données ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#16a34a",
        }).then((res) => {
            if (res.isConfirmed) {
                importEntitiesExcel(selectedFile, false, importAction);
                clearFile();
                setDryRunDone(false);
            }
        });
    };

    // Import direct (sans dry run)
    const handleDirectImport = () => {
        if (!selectedFile) {
            Swal.fire({ icon: "warning", title: "Aucun fichier", text: "Sélectionnez un fichier Excel." });
            return;
        }
        Swal.fire({
            title: "Confirmer l'import",
            text: "Importer directement sans vérification ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ee645f",
        }).then((res) => {
            if (res.isConfirmed) {
                importEntitiesExcel(selectedFile, false, importAction);
                clearFile();
                setDryRunDone(false);
            }
        });
    };

    /* ── FILTRE ─────────────────────────────────── */
    const filteredEntites = useMemo(() => {
        return (entites || []).filter((e) => {
            const matchSearch =
                (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
                (e.code || "").toLowerCase().includes(search.toLowerCase());
            const matchFilter =
                filter === "all"      ? true :
                    filter === "active"   ? e.isActive :
                        !e.isActive;
            return matchSearch && matchFilter;
        });
    }, [entites, search, filter]);

    /* ── Résultats dry run ──────────────────────── */
    const importErrors   = normalizeErrors(importResult);
    const previewRows    = normalizePreviewRows(importResult);
    const successCount   = importResult && (importResult.createdCount || importResult.successCount || 0);
    const skippedCount   = importResult && (importResult.skippedCount || importResult.skipped || 0);
    const failedCount    = importResult && (importResult.failedCount  || importErrors.length || 0);
    const totalRows      = importResult && (importResult.totalRows    || importResult.total  || previewRows.length || 0);

    /* ── RENDER ─────────────────────────────────── */
    return (
        <div className="jm-page">

            {/* ── Header ─────────────────────────── */}
            <div className="jm-header">
                <div>
                    <h2>Gestion des entités</h2>
                    <p className="jm-subtitle">Structure organisationnelle</p>
                </div>
                <div className="jm-header-actions">
                    <button className="jm-btn jm-btn-primary" onClick={openCreate}>
                        + Ajouter une entité
                    </button>
                    <button className="jm-btn jm-btn-outline" onClick={downloadTemplate} disabled={bulkLoading}>
                        <FileSpreadsheet size={14} /> Template
                    </button>
                    <button className="jm-btn jm-btn-outline" onClick={exportEntities} disabled={bulkLoading}>
                        <Download size={14} /> Exporter
                    </button>
                </div>
            </div>

            {/* ── Section Import ─────────────────── */}
            <div className="jm-section">
                <div className="jm-section-header">
                    <h3>Import Excel</h3>
                </div>

                <div className="jm-grid" style={{ marginBottom: 14 }}>
                    <div className="jm-field">
                        <label>Mode d'import</label>
                        <select value={importAction} onChange={(e) => setImportAction(e.target.value)}>
                            <option value="add">Ajout</option>
                            <option value="edit">Mise à jour</option>
                        </select>
                    </div>
                </div>

                <div className="jm-upload-actions-row">
                    <label className="jm-file-label">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            className="jm-file-input"
                        />
                        <span className="jm-file-icon">📂</span>
                        <span>{fileName || "Choisir un fichier .xlsx / .xls"}</span>
                    </label>

                    {fileName && (
                        <button type="button" className="jm-btn jm-btn-outline" onClick={clearFile} disabled={bulkLoading}>
                            <X size={13} /> Changer
                        </button>
                    )}

                    <div className="jm-row-sep" />

                    {/* ── Boutons conditionnels ── */}
                    {!dryRunDone ? (
                        <>
                            <button
                                className="jm-btn jm-btn-outline"
                                onClick={handleVerify}
                                disabled={bulkLoading || !selectedFile}
                            >
                                <Eye size={14} />
                                {bulkLoading ? "Analyse..." : "Vérifier"}
                            </button>
                            <button
                                className="jm-btn jm-btn-primary"
                                onClick={handleDirectImport}
                                disabled={bulkLoading || !selectedFile}
                            >
                                <Upload size={14} />
                                Importer direct
                            </button>
                        </>
                    ) : (
                        /* Après dry run : on remplace par "Confirmer" ou "Réessayer" */
                        <>
                            <button
                                className="jm-btn jm-btn-outline"
                                onClick={() => { setDryRunDone(false); }}
                                disabled={bulkLoading}
                            >
                                <X size={14} /> Annuler
                            </button>
                            <button
                                className="jm-btn jm-btn-primary"
                                onClick={handleConfirmImport}
                                disabled={bulkLoading || successCount === 0}
                            >
                                <Upload size={14} />
                                {bulkLoading
                                    ? "Import en cours..."
                                    : "Confirmer l'import (" + successCount + " ligne" + (successCount !== 1 ? "s" : "") + ")"}
                            </button>
                        </>
                    )}
                </div>

                {bulkLoading && (
                    <div className="jm-loading" style={{ justifyContent: "flex-start", padding: "10px 0 0" }}>
                        <div className="jm-spinner jm-spinner-sm" />
                        <span>Traitement en cours...</span>
                    </div>
                )}

                {/* ── Résultats dry run ── */}
                {dryRunDone && importResult && (
                    <div style={{ marginTop: 16 }}>
                        {/* Stats */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 10, marginBottom: 12 }}>
                            <div className="jm-alert" style={{ margin: 0 }}>
                                <strong>Total lignes :</strong> {totalRows}
                            </div>
                            <div className="jm-alert jm-alert-success" style={{ margin: 0 }}>
                                <strong>Valides :</strong> {successCount}
                            </div>
                            {skippedCount > 0 && (
                                <div className="jm-alert" style={{ margin: 0 }}>
                                    <strong>Ignorées :</strong> {skippedCount}
                                </div>
                            )}
                            {failedCount > 0 && (
                                <div className="jm-alert jm-alert-danger" style={{ margin: 0 }}>
                                    <strong>Erreurs :</strong> {failedCount}
                                </div>
                            )}
                        </div>

                        {/* Erreurs */}
                        {importErrors.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <div className="jm-section-header">
                                    <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <AlertTriangle size={14} /> Erreurs détectées
                                    </h3>
                                </div>
                                <div className="jm-table-wrapper" style={{ margin: 0 }}>
                                    <table className="jm-table">
                                        <thead>
                                        <tr>
                                            <th>Ligne</th>
                                            <th>Colonne</th>
                                            <th>Message</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {importErrors.map((err, i) => (
                                            <tr key={i}>
                                                <td>{err.row  || err.line   || "—"}</td>
                                                <td>{err.field || err.column || "—"}</td>
                                                <td>{err.message || err.error || JSON.stringify(err)}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Preview lignes valides */}
                        {previewRows.length > 0 && (
                            <div>
                                <div className="jm-section-header">
                                    <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <CheckCircle2 size={14} /> Lignes qui seront importées
                                    </h3>
                                </div>
                                <div className="jm-table-wrapper" style={{ margin: 0 }}>
                                    <table className="jm-table">
                                        <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Nom</th>
                                            <th>Parent</th>
                                            <th>Ordre</th>
                                            <th>Actif</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={i}>
                                                <td><span className="jm-code">{row.code}</span></td>
                                                <td>{row.name}</td>
                                                <td>{row.parentEntityName || <span className="jm-muted">—</span>}</td>
                                                <td>{row.displayOrder     || <span className="jm-muted">—</span>}</td>
                                                <td>
                                                    <button type="button" className={"jm-toggle " + (row.isActive ? "on" : "off")}>
                                                        <span className="jm-toggle-knob" />
                                                        <span>{row.isActive ? "Actif" : "Inactif"}</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Toolbar ────────────────────────── */}
            <div className="jm-toolbar">
                <input
                    className="jm-search"
                    placeholder="Rechercher (code, nom...)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="jm-filter-tabs">
                    {[
                        { key: "all",      label: "Tous"     },
                        { key: "active",   label: "Actifs"   },
                        { key: "inactive", label: "Inactifs" },
                    ].map((f) => (
                        <button
                            key={f.key}
                            className={"jm-filter-tab " + (filter === f.key ? "active" : "")}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="jm-meta">
                    {loading
                        ? "Chargement..."
                        : filteredEntites.length + " entité" + (filteredEntites.length !== 1 ? "s" : "")}
                </div>
            </div>

            {/* Alerts */}
            {message && <div className="jm-alert jm-alert-success">{message}</div>}
            {error   && <div className="jm-alert jm-alert-danger">{error}</div>}

            {/* ── Table ──────────────────────────── */}
            <div className="jm-table-wrapper">
                {loading ? (
                    <div className="jm-loading">
                        <div className="jm-spinner" />
                        <span>Chargement...</span>
                    </div>
                ) : filteredEntites.length === 0 ? (
                    <div className="jm-empty">Aucune entité trouvée</div>
                ) : (
                    <table className="jm-table">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Nom</th>
                            <th>Parent</th>
                            <th>Ordre</th>
                            <th>Statut</th>
                            <th style={{ width: 120, textAlign: "right" }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredEntites.map((e) => (
                            <tr key={e.id}>
                                <td><span className="jm-code">{e.code}</span></td>
                                <td className="jm-name">{e.name}</td>
                                <td className="jm-muted">{e.parentEntityName || <span className="jm-muted">—</span>}</td>
                                <td className="jm-muted">{e.displayOrder != null ? e.displayOrder : <span className="jm-muted">—</span>}</td>
                                <td>
                                    <button type="button" className={"jm-toggle " + (e.isActive ? "on" : "off")}>
                                        <span className="jm-toggle-knob" />
                                        <span>{e.isActive ? "Actif" : "Inactif"}</span>
                                    </button>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    <div className="jm-row-actions">
                                        <button className="jm-link-btn" onClick={() => openEdit(e)} title="Modifier">
                                            <Pencil size={15} />
                                        </button>
                                        <button className="jm-link-btn danger" onClick={() => handleDelete(e.id, e.name)} title="Supprimer">
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Modal ──────────────────────────── */}
            <EntiteModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                editingEntite={editingEntite}
                loading={loading}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    entites:      state.entite.entites      || [],
    loading:      state.entite.loading,
    bulkLoading:  state.entite.bulkLoading,
    importResult: state.entite.importResult,
    message:      state.entite.message,
    error:        state.entite.error,
});

const mapDispatchToProps = (dispatch) => ({
    getAllEntites:        ()                   => dispatch(getAllEntitesRequest()),
    createEntite:        (data)               => dispatch(createEntiteRequest(data)),
    updateEntite:        (id, data)           => dispatch(updateEntiteRequest(id, data)),
    deleteEntite:        (id)                 => dispatch(deleteEntiteRequest(id)),
    importEntitiesExcel: (file, dry, act)     => dispatch(importEntitiesExcelRequest(file, dry, act)),
    exportEntities:      ()                   => dispatch(exportEntitiesRequest()),
    downloadTemplate:    ()                   => dispatch(downloadEntitiesTemplateRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EntiteManagement);